import { useState } from "react"
import { cn } from "@/lib/utils"
import {
  hasPageFacts,
  recommendationKind,
  type PageAnalysisKind,
  type PageAnalysisRecommendation,
  type PageFacts,
} from "@/lib/api/onboardingTests"

/**
 * The plan step has no element geometry: `PageAnalysisRecommendation` carries a
 * CSS selector, not a bounding box. The bands below are the honest approximation
 * of where that element sits on a landing page, and they are tuned to line up
 * with `SkeletonPage` so the fallback and the real screenshot read the same.
 */
export type HighlightKind = PageAnalysisKind
export type HighlightBand = { top: number; left: number; width: number; height: number }

export const HIGHLIGHT_BANDS: Record<HighlightKind, HighlightBand> = {
  headline: { top: 15, left: 9, width: 62, height: 13 },
  cta: { top: 37, left: 9, width: 26, height: 10 },
  block: { top: 30, left: 9, width: 82, height: 30 },
}

/**
 * The single orange fill inside onboarding. Measured to hold 4.5:1 against a
 * white label, where the app-wide `--primary` measures 4.06:1.
 */
const ACTION_FILL = "var(--onboarding-action, oklch(0.54 0.21 42))"

type RecShape = Pick<
  PageAnalysisRecommendation,
  "element_type" | "element_selector" | "change_type" | "variant_value" | "expected_impact"
>

export function highlightKindFor(rec: Partial<RecShape>): HighlightKind {
  return recommendationKind(rec)
}

export function highlightBandFor(rec: Partial<RecShape>): HighlightBand {
  return HIGHLIGHT_BANDS[highlightKindFor(rec)]
}

/**
 * Names the kind of change, never the copy itself. Used as the chip on the
 * facts panel, where the proposed copy already occupies the row beneath it.
 */
export function changeLabelFor(rec: Partial<RecShape>): string {
  const kind = highlightKindFor(rec)
  if (rec.change_type === "css" || rec.change_type === "layout") {
    return kind === "cta" ? "Restyled CTA" : "Restyled section"
  }
  if (kind === "cta") return "New CTA label"
  if (kind === "headline") return "New headline copy"
  return "Revised section"
}

/** The B-side chip. Real proposed copy when the analysis returned it, otherwise a label for the change. */
export function variantChipFor(rec: Partial<RecShape>): string {
  const proposed = rec.variant_value?.trim()
  if (proposed) return proposed.length > 58 ? `${proposed.slice(0, 55)}…` : proposed
  return changeLabelFor(rec)
}

/**
 * Only render a figure the analysis actually produced. Never invent one.
 * Retained for the editor surfaces; the plan card itself now carries a single
 * sourced line instead of a per-recommendation projection.
 */
export function expectedLiftFor(rec: Partial<RecShape>): string | null {
  const source = rec.expected_impact ?? ""
  const range = source.match(/(\d+(?:\.\d+)?)\s*%?\s*(?:to|–|-)\s*(\d+(?:\.\d+)?)\s*%/i)
  if (range) return `+${range[1]} to ${range[2]}%`
  const single = source.match(/(\d+(?:\.\d+)?)\s*%/)
  if (single) return `+${single[1]}%`
  return null
}

export function SkeletonPage() {
  return (
    <div aria-hidden className="absolute inset-0 flex flex-col gap-1.5 bg-muted/20 p-2.5">
      <div className="flex items-center gap-1">
        <div className="h-1.5 w-5 rounded-full bg-foreground/15" />
        <div className="ml-auto h-1.5 w-3 rounded-full bg-foreground/8" />
        <div className="h-1.5 w-3 rounded-full bg-foreground/8" />
        <div className="h-1.5 w-4 rounded-sm bg-foreground/12" />
      </div>
      <div className="mt-1 h-2 w-4/5 rounded-full bg-foreground/12" />
      <div className="h-1.5 w-3/5 rounded-full bg-foreground/8" />
      <div className="h-2.5 w-12 rounded-sm bg-foreground/15" />
      <div className="mt-0.5 flex-1 rounded-sm bg-foreground/6" />
    </div>
  )
}

/** The chip that names the change. White on the onboarding action fill, 10px floor. */
function VariantChip({ text, className }: { text: string; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block max-w-full truncate rounded-sm px-1 py-px text-[11px] leading-[1.35] font-medium text-white",
        className,
      )}
      style={{ backgroundColor: ACTION_FILL }}
    >
      {text}
    </span>
  )
}

export function HighlightOverlay({ band, chip }: { band: HighlightBand; chip?: string | null }) {
  return (
    <>
      <div
        aria-hidden
        className="absolute rounded-[3px] border bg-primary/5"
        style={{
          top: `${band.top}%`,
          left: `${band.left}%`,
          width: `${band.width}%`,
          height: `${band.height}%`,
          borderColor: ACTION_FILL,
        }}
      />
      {chip ? (
        <span
          className="absolute max-w-[80%]"
          // Inside the highlighted region, below its top edge, so the page text
          // under the chip stays legible.
          style={{ top: `calc(${band.top}% + 3px)`, left: `calc(${band.left}% + 3px)` }}
        >
          <VariantChip text={chip} />
        </span>
      ) : null}
    </>
  )
}

/**
 * The P0 fallback: when there is no screenshot of the page, the frame quotes
 * what the crawl actually read instead of drawing a wireframe of a page we
 * have never seen. B repeats the same panel with one line changed.
 */
function FactsPanel({
  facts,
  page,
  changed,
  highlight,
  changeLabel,
  variantText,
}: {
  facts: PageFacts
  page: string
  /** The row the test touches. Ordered first in BOTH frames so A and B line up. */
  changed?: HighlightKind | null
  /** Only the B frame draws the change. */
  highlight?: boolean
  /** Names the change. Sits in the chip, above the proposed line. */
  changeLabel?: string | null
  variantText?: string | null
}) {
  const all: Array<{ kind: HighlightKind; label: string; value: string }> = []
  if (facts.headline) all.push({ kind: "headline", label: "Headline", value: facts.headline })
  if (facts.cta) all.push({ kind: "cta", label: "Primary CTA", value: facts.cta })
  if (facts.price) all.push({ kind: "block", label: "Price", value: facts.price })

  // A block-level change has no row of its own unless price supplied one.
  const changedKind =
    changed && all.some((row) => row.kind === changed) ? changed : changed ? all[0]?.kind : null
  // The touched row leads, so a 16:10 frame that can only fit one row on a
  // phone still shows the line the test is about.
  const rows = changedKind
    ? [...all.filter((row) => row.kind === changedKind), ...all.filter((row) => row.kind !== changedKind)]
    : all

  // The panel stops above the A/B badge's strip, so a solid badge never lands
  // on a fact.
  return (
    <div className="absolute inset-x-0 top-0 bottom-6 flex flex-col gap-1 overflow-hidden bg-card px-2 pt-2">
      {/* The domain label is a luxury a 16:10 frame cannot afford on a phone. */}
      <p className="hidden truncate text-[8px] font-medium text-muted-foreground sm:block">
        {page}
      </p>
      <div className="flex min-h-0 flex-1 flex-col gap-1">
        {rows.map((row, i) => {
          // Secondary rows only appear where the frame is tall enough for them.
          const secondary = i > 0 ? "hidden sm:block" : ""
          if (!(highlight && row.kind === changedKind)) {
            return (
              <div key={row.kind} className={cn("min-w-0", secondary)}>
                <p className="text-[8px] leading-tight text-muted-foreground">{row.label}</p>
                <p className="truncate text-[11px] leading-tight text-foreground/90">{row.value}</p>
              </div>
            )
          }
          return (
            <div
              key={row.kind}
              className={cn("min-w-0 rounded-[3px] border bg-primary/5 px-1 py-1", secondary)}
              style={{ borderColor: ACTION_FILL }}
            >
              <VariantChip text={changeLabel || "Changed line"} className="max-w-[95%]" />
              <p className="mt-0.5 truncate text-[11px] leading-tight text-foreground">
                {variantText || row.value}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

type FrameProps = {
  label: "A" | "B"
  imageUrl?: string | null
  alt: string
  band?: HighlightBand
  chip?: string | null
  facts?: PageFacts | null
  pageLabel: string
  changed?: HighlightKind | null
  highlight?: boolean
  changeLabel?: string | null
  variantText?: string | null
}

function Frame({
  label,
  imageUrl,
  alt,
  band,
  chip,
  facts,
  pageLabel,
  changed,
  highlight,
  changeLabel,
  variantText,
}: FrameProps) {
  const [loaded, setLoaded] = useState(false)
  const isVariant = label === "B"
  const showImage = Boolean(imageUrl && loaded)
  const showFacts = !showImage && hasPageFacts(facts)

  return (
    <div className="relative aspect-[16/10] overflow-hidden rounded-md border border-border/40 bg-card">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={alt}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(false)}
          className={cn(
            "h-full w-full object-cover object-top transition-opacity duration-300 motion-reduce:transition-none",
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
      ) : null}
      {showImage ? null : showFacts && facts ? (
        <FactsPanel
          facts={facts}
          page={pageLabel}
          changed={changed}
          highlight={highlight}
          changeLabel={changeLabel}
          variantText={variantText}
        />
      ) : (
        <SkeletonPage />
      )}
      {band && !showFacts ? <HighlightOverlay band={band} chip={chip} /> : null}
      <span
        className={cn(
          "absolute bottom-1.5 left-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-sm px-1 text-[11px] font-semibold",
          isVariant
            ? "text-white"
            : "border border-border/60 bg-background/90 text-muted-foreground",
        )}
        style={isVariant ? { backgroundColor: ACTION_FILL } : undefined}
      >
        {label}
      </span>
    </div>
  )
}

type OnboardingTestCardProps = {
  rec: PageAnalysisRecommendation
  index: number
  /** Real page screenshot, from heatmap-pages `desktop_screenshot_url` or the Firecrawl before-shot. */
  controlImageUrl?: string | null
  /** Firecrawl after-shot, when the generation job captured one. */
  variantImageUrl?: string | null
  /** Quoted crawl facts, used when there is no screenshot yet. */
  facts?: PageFacts | null
  pageLabel?: string
  onOpenTest?: () => void
}

export function OnboardingTestCard({
  rec,
  index,
  controlImageUrl,
  variantImageUrl,
  facts,
  pageLabel,
  onOpenTest,
}: OnboardingTestCardProps) {
  const page = pageLabel?.trim() || "your page"
  const band = highlightBandFor(rec)
  const kind = highlightKindFor(rec)
  const chip = variantChipFor(rec)
  const changeLabel = changeLabelFor(rec)
  // A real after-shot already shows the change, so the drawn outline is only for the synthetic B.
  const syntheticVariant = !variantImageUrl

  return (
    <div className="rounded-lg border border-border/40 bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-muted-foreground">
          Test {index + 1}
        </p>
        {onOpenTest ? (
          <button
            type="button"
            onClick={onOpenTest}
            className="text-[11px] text-foreground/80 underline-offset-2 hover:text-foreground hover:underline"
          >
            Open this test
          </button>
        ) : null}
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2">
        <Frame
          label="A"
          imageUrl={controlImageUrl}
          alt={`Control: ${page} as it is today`}
          facts={facts}
          pageLabel={page}
          changed={syntheticVariant ? kind : null}
        />
        <Frame
          label="B"
          imageUrl={variantImageUrl ?? controlImageUrl}
          alt={`Variant: ${page} with ${chip.toLowerCase()}`}
          band={syntheticVariant ? band : undefined}
          chip={syntheticVariant ? chip : undefined}
          facts={facts}
          pageLabel={page}
          changed={syntheticVariant ? kind : null}
          highlight={syntheticVariant}
          changeLabel={syntheticVariant ? changeLabel : null}
          variantText={syntheticVariant ? chip : null}
        />
      </div>

      <p className="mt-2.5 truncate text-sm font-medium text-foreground">{rec.title}</p>
    </div>
  )
}
